import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MessageService } from 'primeng/api';
import { DataService } from 'src/app/services/data.service';
import { ValidationRunResult } from './validation-run-result';

/**
 * Reusable, entity-agnostic Validate button. Given entityType/section/entityId
 * it calls the generic validation endpoint, manages its own loading state, and
 * emits the result. No per-entity logic lives here — the Series page is just its
 * first consumer.
 */
@Component({
  selector: 'app-validate-button',
  templateUrl: './validate-button.component.html',
})
export class ValidateButtonComponent {
  /** Registered Mongoose model name, e.g. 'Series'. */
  @Input() entityType!: string;
  /** Optional section key; omit to validate all sections. */
  @Input() section?: string;
  @Input() entityId!: number;
  /** Unique Mongo _id; preferred over entityId (numeric ids can collide). */
  @Input() entityObjectId?: string;
  /** Optional override for the button label. */
  @Input() label?: string;
  @Input() styleClass = 'p-button-help';

  @Output() results = new EventEmitter<ValidationRunResult>();
  @Output() failed = new EventEmitter<unknown>();

  loading = false;

  constructor(
    private dataService: DataService,
    private messageService: MessageService,
  ) {}

  get buttonLabel(): string {
    return this.label ?? (this.section ? 'Validate' : 'Validate All');
  }

  async validate(): Promise<void> {
    if (this.loading || !this.entityType) return;
    if (this.entityId == null && !this.entityObjectId) return;
    this.loading = true;
    try {
      const result = await this.dataService.validateEntity(
        this.entityType,
        this.entityId,
        this.section,
        this.entityObjectId,
      );
      this.results.emit(result);
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation failed',
        detail: 'Could not run validation. Please try again.',
      });
      this.failed.emit(error);
    } finally {
      this.loading = false;
    }
  }
}
